import api from './api';
import { authService } from './authService';
import { ApiResponse, PageResponse } from '../types/api';
import {
  PaymentRequestDTO,
  PaymentResponseDTO,
  PaymentType,
  PurchasePaymentStats,
  PaymentFilters,
} from '../types/payment';

// Bases oficiais
const baseUrl = '/v1/pagamentos';
const typesBaseUrl = '/v1/tipos-pagamento';

export const paymentService = {
  // Listar tipos de pagamento
  async getTypes(): Promise<ApiResponse<PaymentType[]>> {
    const response = await api.get<ApiResponse<PaymentType[]>>(typesBaseUrl);
    return response.data;
  },
  // Criar pagamento (Compra ou Venda) via DTO unificado
  async create(data: PaymentRequestDTO): Promise<ApiResponse<PaymentResponseDTO>> {
    // Alguns ambientes exigem a indicação explícita da origem (COMPRA/VENDA)
    const origem: 'COMPRA' | 'VENDA' | undefined = data.vendaId
      ? 'VENDA'
      : data.compraId
      ? 'COMPRA'
      : undefined;
    const payload = origem ? { ...data, origem } : data;
    const response = await api.post<ApiResponse<PaymentResponseDTO>>(baseUrl, payload);
    return response.data;
  },

  // Listar pagamentos por compra (helper que usa filtros da listagem geral)
  async listByPurchase(compraId: string, params?: { page?: number; size?: number }): Promise<ApiResponse<PageResponse<PaymentResponseDTO>>> {
    const searchParams = new URLSearchParams();
    searchParams.append('compraId', compraId);
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    const response = await api.get<ApiResponse<PageResponse<PaymentResponseDTO>>>(`${baseUrl}?${searchParams.toString()}`);
    return response.data;
  },

  // Listar pagamentos por venda (helper que usa filtros da listagem geral)
  async listBySale(vendaId: string, params?: { page?: number; size?: number }): Promise<ApiResponse<PageResponse<PaymentResponseDTO>>> {
    const searchParams = new URLSearchParams();
    searchParams.append('vendaId', vendaId);
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    const response = await api.get<ApiResponse<PageResponse<PaymentResponseDTO>>>(`${baseUrl}?${searchParams.toString()}`);
    return response.data;
  },

  // Listar pagamentos por venda usando rota dedicada
  async listBySalePath(vendaId: string): Promise<ApiResponse<PageResponse<PaymentResponseDTO> | PaymentResponseDTO[]>> {
    const response = await api.get<ApiResponse<PageResponse<PaymentResponseDTO> | PaymentResponseDTO[]>>(`${baseUrl}/venda/${vendaId}`);
    return response.data;
  },

  // Listar pagamentos por compra usando rota dedicada
  async listByPurchasePath(compraId: string): Promise<ApiResponse<PageResponse<PaymentResponseDTO> | PaymentResponseDTO[]>> {
    const response = await api.get<ApiResponse<PageResponse<PaymentResponseDTO> | PaymentResponseDTO[]>>(`${baseUrl}/compra/${compraId}`);
    return response.data;
  },

  // Confirmar pagamento (este ambiente usa "/confirmar" e espera usuarioId na query)
  async efetuar(id: string, observacao?: string): Promise<ApiResponse<PaymentResponseDTO>> {
    // Obter usuarioId como UUID diretamente do contexto de autenticação
    let usuarioId = authService.getLoginData()?.usuarioId || authService.getUser()?.id || null;
    if (!usuarioId) {
      try {
        const raw = localStorage.getItem('loginData') || localStorage.getItem('user');
        if (raw) {
          const obj = JSON.parse(raw);
          usuarioId = obj?.usuarioId || obj?.id || usuarioId;
        }
      } catch {}
    }

    // Construir query params conforme documentação (usuarioId UUID + observacao opcional)
    const params = new URLSearchParams();
    if (usuarioId) params.append('usuarioId', String(usuarioId));
    if (observacao) params.append('observacao', observacao);
    const qs = params.toString();

    const url = qs ? `${baseUrl}/${id}/confirmar?${qs}` : `${baseUrl}/${id}/confirmar`;

    // Enviar POST sem body (API usa parâmetros de query)
    const response = await api.post<ApiResponse<PaymentResponseDTO>>(url);
    return response.data;
  },

  // Cancelar pagamento
  async cancel(id: string, motivo?: string): Promise<ApiResponse<PaymentResponseDTO>> {
    const params = new URLSearchParams();
    if (motivo) params.append('motivo', motivo);
    const qs = params.toString();
    const response = await api.post<ApiResponse<PaymentResponseDTO>>(`${baseUrl}/${id}/cancelar${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  async getStats(): Promise<ApiResponse<PurchasePaymentStats>> {
    const response = await api.get<ApiResponse<PurchasePaymentStats>>(`${baseUrl}/estatisticas`);
    return response.data;
  },

  async getMostUsedTypes(limit?: number): Promise<ApiResponse<PaymentType[]>> {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());
    const qs = params.toString();
    const url = qs ? `${baseUrl}/tipos-mais-utilizados?${qs}` : `${baseUrl}/tipos-mais-utilizados`;
    const response = await api.get<ApiResponse<PaymentType[]>>(url);
    return response.data;
  },

  async list(params?: PaymentFilters & { page?: number; size?: number }): Promise<ApiResponse<PageResponse<PaymentResponseDTO>>> {
    const searchParams = new URLSearchParams();
    if (params?.compraId) searchParams.append('compraId', params.compraId);
    if (params?.vendaId) searchParams.append('vendaId', params.vendaId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.tipoPagamentoId) searchParams.append('tipoPagamentoId', params.tipoPagamentoId);
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    const response = await api.get<ApiResponse<PageResponse<PaymentResponseDTO>>>(`${baseUrl}?${searchParams.toString()}`);
    return response.data;
  },
};

export default paymentService;